import React, { useEffect, useState } from "react";

import axios from "axios";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";

import { CheckoutForm } from "./checkout-form";
import { useTranslation } from 'react-i18next';

interface IProps {
  paymentId: string;
  companyStripeAccountId: string;
  customAmount?: number | null;
  amountDue?: number | null;
  onSuccess?: () => void;
  onAlreadyPaid?: () => void;
}

const PaymentForm = ({
  paymentId,
  companyStripeAccountId,
  customAmount,
  amountDue,
  onSuccess,
  onAlreadyPaid,
}: IProps) => {
  const { t } = useTranslation();
  const tBase = "pages.unprotected.deposit-request.payment-form.index";
  const tr = (key: string) => t(`${tBase}.${key}`);

  const [stripePromise, setStripePromise] = useState<null | Promise<any>>(null);
  const [clientSecret, setClientSecret] = useState("");
  const [isPayable, setIsPayable] = useState(false);
  const [loading, setLoading] = useState(false);
  const [blockedMessage, setBlockedMessage] = useState<string | null>(null);

  useEffect(() => {
    const publishableKey = process.env.REACT_APP_STRIPE;
    if (publishableKey)
      setStripePromise(
        loadStripe(process.env.REACT_APP_STRIPE!, {
          stripeAccount: companyStripeAccountId,
        })
      );
  }, [companyStripeAccountId]);

  const getPaymentIntent = async () => {
    try {
      setLoading(true);
      setBlockedMessage(null);

      const res = await axios.get(
        `${process.env.REACT_APP_API}/api/v1/consumer/deposit-request/${paymentId}/payment-intent-direct`,
        {
          params: {
            customAmount: customAmount ? customAmount : null,
            amountDue: amountDue ? amountDue : null,
          }
        }
      );
      
      setClientSecret(res.data.secret);
      setIsPayable(res.data.payable);
    } catch (error: any) {
      const status = error?.response?.status;
      if (status === 409) {
        setIsPayable(false);
        setBlockedMessage(tr("This deposit request has already been paid."));
        onAlreadyPaid?.();
      } else if (status === 400) {
        setIsPayable(false);
        const apiMessage = error?.response?.data?.error?.message;
        setBlockedMessage(
          apiMessage
            ? t(`${tBase}.${apiMessage}`, { defaultValue: apiMessage })
            : tr("This payment amount is no longer valid.")
        );
      } else if (status === 405) {
        setIsPayable(false);
        setBlockedMessage(tr("Invoice Payment Disabled"));
      } else {
        setIsPayable(false);
        setBlockedMessage(tr("Unable to start payment. Please try again."));
      }
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getPaymentIntent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentId]);

  return (
    <>
      {loading ? (
        <div>{tr("loading...")}</div>
      ) : !isPayable ? (
        <div>{blockedMessage ?? tr("Invoice Payment Disabled")}</div>
      ) : (
        <>
          {clientSecret && stripePromise && (
            // @ts-ignore
            <Elements stripe={stripePromise} options={{ clientSecret }}>
              <CheckoutForm customAmount={customAmount} onSuccess={onSuccess} />
            </Elements>
          )}
        </>
      )}
    </>
  );
};

export default PaymentForm;
